import React from 'react'
import { Crown, Check, Clock, AlertTriangle, Info, MessageSquare } from 'lucide-react'
import type { TeamStatsMember } from './teamStats'
import { type RosterStatus, formatLastActive, daysSince } from './rosterStatus'

interface RosterTableProps {
  members: TeamStatsMember[]
  statuses: Map<number, RosterStatus>
  now: number
}

const STATUS_CONFIG: Record<RosterStatus, { label: string; icon: React.ReactNode; style: React.CSSProperties }> = {
  top: { label: 'TOP', icon: <Crown size={10} />, style: { background: 'var(--m2-brand)', color: 'var(--m2-lime)' } },
  ontrack: { label: 'ON TRACK', icon: <Check size={10} />, style: { background: 'var(--m2-mint)', color: 'var(--m2-ink)' } },
  stalled: { label: 'STALLED', icon: <Clock size={10} />, style: { background: 'var(--m2-amber-bg)', color: 'var(--m2-amber-ink)' } },
  risk: { label: 'AT RISK', icon: <AlertTriangle size={10} />, style: { background: 'var(--m2-rose)', color: 'var(--m2-rose-ink)' } },
}

const AVATAR_STYLE: Record<RosterStatus, React.CSSProperties> = {
  top: { background: 'var(--m2-brand)', color: 'var(--m2-lime)' },
  ontrack: { background: 'var(--m2-mint)', color: 'var(--m2-ink)' },
  stalled: { background: 'var(--m2-amber-bg)', color: 'var(--m2-amber-ink)' },
  risk: { background: 'var(--m2-rose)', color: 'var(--m2-rose-ink)' },
}

const memberName = (m: TeamStatsMember): string => {
  const name = [m.firstName, m.lastName].filter(Boolean).join(' ').trim()
  return name || m.email
}

const memberInitials = (m: TeamStatsMember): string => {
  if (m.firstName || m.lastName) return `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`.toUpperCase()
  return m.email.slice(0, 2).toUpperCase()
}

const StarsInline: React.FC<{ score: number }> = ({ score }) => {
  const full = Math.round(score)
  return (
    <div className="text-[10px] leading-none tracking-[-1px]" style={{ color: 'var(--m2-amber)' }}>
      {'★'.repeat(Math.min(5, full))}
      <span style={{ color: '#E0E5DE' }}>{'★'.repeat(Math.max(0, 5 - full))}</span>
    </div>
  )
}

const GRID = 'grid grid-cols-[1.6fr_0.6fr_1fr_0.9fr_0.9fr] min-[800px]:grid-cols-[2.2fr_1fr_1.3fr_1fr_1fr] gap-1.5 min-[800px]:gap-2.5 items-center px-2.5 min-[800px]:px-3.5'

// Team roster table from the mockup: rep, sessions, score stars, last active, status tag
const RosterTable: React.FC<RosterTableProps> = ({ members, statuses, now }) => {
  const sorted = [...members].sort((a, b) => b.avgScore - a.avgScore)
  const attentionCount = sorted.filter((m) => statuses.get(m.userId) === 'stalled' || statuses.get(m.userId) === 'risk').length

  return (
    <>
      <div className="rounded-[10px] overflow-hidden" style={{ background: 'var(--m2-card)', border: '1px solid var(--m2-line)' }}>
        <div className={`${GRID} py-2 text-[9.5px] font-semibold tracking-[1px]`} style={{ background: '#F5F8F5', color: 'var(--m2-muted)' }}>
          <div>REP</div>
          <div className="text-center">SESSIONS</div>
          <div>AVG SCORE</div>
          <div>LAST ACTIVE</div>
          <div>STATUS</div>
        </div>

        {sorted.map((m, i) => {
          const status = statuses.get(m.userId) || 'ontrack'
          const inactiveDays = daysSince(m.lastActiveAt, now)
          const config = STATUS_CONFIG[status]
          return (
            <div
              key={m.userId}
              className={`${GRID} py-2.5`}
              style={{ borderTop: '1px solid var(--m2-line-soft)', background: status === 'top' ? '#FAFCF7' : undefined }}
              data-testid={`roster-row-${i}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center font-semibold text-[10px] shrink-0" style={AVATAR_STYLE[status]}>
                  {memberInitials(m)}
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold leading-tight truncate" style={{ color: 'var(--m2-ink)' }}>
                    {memberName(m)}
                  </div>
                  <div className="text-[10px] mt-px" style={{ color: 'var(--m2-muted)' }}>
                    {m.masteredCount} mastered
                  </div>
                </div>
              </div>
              <div className="text-center text-[13px] font-bold" style={{ color: 'var(--m2-ink)' }}>
                {m.totalSessions}
              </div>
              <div className="flex items-center gap-1.5">
                <StarsInline score={m.avgScore} />
                <div className="text-[11.5px] font-bold" style={{ color: 'var(--m2-ink)' }}>
                  {m.avgScore.toFixed(1)}
                </div>
              </div>
              <div className="text-[11px] font-medium" style={{ color: inactiveDays !== null && inactiveDays >= 14 ? 'var(--m2-rose-ink)' : 'var(--m2-ink)' }}>
                {formatLastActive(m.lastActiveAt, now)}
              </div>
              <div>
                <div className="flex items-center gap-1 text-[9px] font-bold px-1.5 py-[3px] rounded-[3px] w-fit tracking-[0.5px]" style={config.style}>
                  {config.icon}
                  {config.label}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex items-center justify-between mt-3 px-0.5">
        <div className="flex items-center gap-1.5 text-[10.5px]" style={{ color: 'var(--m2-muted)' }}>
          <Info size={13} />
          Reps inactive 14+ days flagged &quot;at risk&quot;
        </div>
        {attentionCount > 0 && (
          <button
            type="button"
            className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-[5px] rounded-[5px] bg-transparent cursor-default"
            style={{ color: 'var(--m2-ink)', border: '1px solid var(--m2-brand)' }}
          >
            <MessageSquare size={13} />
            Send nudge to {attentionCount} {attentionCount === 1 ? 'rep' : 'reps'}
          </button>
        )}
      </div>
    </>
  )
}

export default RosterTable
